/**
 * Service pour gérer l'historique des modifications de tarifs
 */
import { apiFetch } from './httpClient';

export interface ModificationHistorique {
  id: number;
  type_modification: 'tarifs_coefficients' | 'matieres' | 'fournitures' | 'mousses';
  description: string;
  date_modification: string;
  utilisateur: string;
}

export interface VerificationResult {
  hasModifications: boolean;
  modifications: ModificationHistorique[];
  lastModificationDate: string | null;
}

class HistoriqueService {

  /**
   * Vérifie s'il y a eu des modifications de tarifs depuis une date de référence
   */
  async verifierModifications(dateReference: string): Promise<VerificationResult> {
    try {
      const result = await apiFetch<any>(`/historique/verifier?date_reference=${encodeURIComponent(dateReference)}`);
      
      // ✅ ADAPTATION: Mapper la réponse du backend vers notre interface
      return {
        hasModifications: result.modifications_trouvees || false,
        modifications: result.modifications || [],
        lastModificationDate: result.derniere_modification?.timestamp || null
      };
    } catch (error) {
      console.error('Erreur lors de la vérification des modifications:', error);
      // Retourner un résultat par défaut en cas d'erreur
      return {
        hasModifications: false,
        modifications: [],
        lastModificationDate: null
      };
    }
  }

  /**
   * Vérifie s'il y a eu des modifications depuis une date (alias pour compatibilité)
   */
  async verifierModificationsDepuis(dateReference: string): Promise<VerificationResult> {
    return this.verifierModifications(dateReference);
  }

  /**
   * Récupère l'historique complet des modifications
   */
  async obtenirHistorique(limit: number = 100): Promise<ModificationHistorique[]> {
    try {
      const result = await apiFetch<ModificationHistorique[]>(`/historique?limit=${limit}`);
      return result;
    } catch (error) {
      console.error('Erreur lors de la récupération de l\'historique:', error);
      return [];
    }
  }

  /**
   * Enregistre une modification dans l'historique
   */
  async enregistrerModification(
    typeModification: 'tarifs_coefficients' | 'matieres' | 'fournitures' | 'mousses',
    description: string,
    utilisateur: string = 'Utilisateur'
  ): Promise<ModificationHistorique | null> {
    try {
      const result = await apiFetch<ModificationHistorique>('/historique/enregistrer', {
        method: 'POST',
        body: JSON.stringify({
          type_modification: typeModification,
          description,
          utilisateur
        })
      });

      return result;
    } catch (error) {
      console.error('Erreur lors de l\'enregistrement de la modification:', error);
      return null;
    }
  }
}

// Exporter une instance unique du service
export const historiqueService = new HistoriqueService();
